import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Upload, Trash2, Plus, X, ExternalLink, Loader2, Image as ImageIcon, Globe } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export default function LandingConfig() {
  const [cfg, setCfg] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newFeature, setNewFeature] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([api.get("/landing/config"), api.get("/plans")]);
      setCfg(c.data); setPlans(p.data);
    } catch (e) { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/landing/config", cfg);
      toast.success("Site atualizado! Acesse /loja para ver");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const uploadImages = async (files) => {
    const arr = Array.from(files);
    for (const f of arr) {
      if (!f.type.startsWith("image/")) { toast.error("Só imagens"); continue; }
      if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} > 4MB`); continue; }
      const r = new FileReader();
      const dataUrl = await new Promise((res) => { r.onload = () => res(r.result); r.readAsDataURL(f); });
      setCfg((c) => ({ ...c, hero_images: [...(c.hero_images || []), dataUrl] }));
    }
    toast.success("Imagens adicionadas — clique Salvar");
  };
  const removeImage = (idx) => setCfg((c) => ({ ...c, hero_images: c.hero_images.filter((_, i) => i !== idx) }));
  const addFeature = () => { if (!newFeature.trim()) return; setCfg((c) => ({ ...c, features: [...(c.features || []), newFeature.trim()] })); setNewFeature(""); };
  const removeFeature = (idx) => setCfg((c) => ({ ...c, features: c.features.filter((_, i) => i !== idx) }));
  const togglePlan = (id) => setCfg((c) => {
    const cur = c.plans_visible || [];
    return { ...c, plans_visible: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  if (loading || !cfg) return <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400"/></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PageHeader title="Site de Vendas" subtitle="Configure a página pública acessada em /loja"/>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.open("/loja", "_blank")} data-testid="landing-preview"><ExternalLink className="w-4 h-4 mr-2"/>Ver site</Button>
          <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="landing-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Salvando</> : <><Save className="w-4 h-4 mr-2"/>Salvar alterações</>}
          </Button>
        </div>
      </div>

      <Card className="p-5 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-rose-600"/>
          <h3 className="font-heading font-bold text-slate-900">Conteúdo principal</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Título (H1)</Label><Input value={cfg.title || ""} onChange={(e)=>setCfg({...cfg, title: e.target.value})} data-testid="landing-title"/></div>
          <div className="md:col-span-2"><Label>Subtítulo</Label><Input value={cfg.subtitle || ""} onChange={(e)=>setCfg({...cfg, subtitle: e.target.value})} data-testid="landing-subtitle"/></div>
          <div className="md:col-span-2"><Label>Descrição</Label>
            <textarea rows={3} value={cfg.description || ""} onChange={(e)=>setCfg({...cfg, description: e.target.value})} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="landing-description"/>
          </div>
          <div>
            <Label>Cor principal (hex)</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={cfg.primary_color || "#E11D48"} onChange={(e)=>setCfg({...cfg, primary_color: e.target.value})} className="h-11 w-16 p-1" data-testid="landing-color"/>
              <Input value={cfg.primary_color || "#E11D48"} onChange={(e)=>setCfg({...cfg, primary_color: e.target.value})} className="h-11 flex-1 font-mono text-xs uppercase" data-testid="landing-color-hex"/>
            </div>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {["#E11D48","#DC2626","#B91C1C","#0F172A","#7C3AED","#2563EB","#059669","#F59E0B"].map(c=>(
                <button key={c} type="button" onClick={()=>setCfg({...cfg, primary_color: c})} className={`w-7 h-7 rounded-full border-2 transition ${cfg.primary_color?.toLowerCase()===c.toLowerCase() ? "border-slate-900 scale-110" : "border-white shadow"}`} style={{ background: c }} data-testid={`landing-primary-preset-${c.slice(1)}`}/>
              ))}
            </div>
          </div>
          <div>
            <Label>Cor secundária (accents, botões)</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={cfg.secondary_color || "#F59E0B"} onChange={(e)=>setCfg({...cfg, secondary_color: e.target.value})} className="h-11 w-16 p-1" data-testid="landing-secondary-color"/>
              <Input value={cfg.secondary_color || "#F59E0B"} onChange={(e)=>setCfg({...cfg, secondary_color: e.target.value})} className="h-11 flex-1 font-mono text-xs uppercase" data-testid="landing-secondary-hex"/>
            </div>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {["#F59E0B","#FBBF24","#0F172A","#111827","#F5F5F4","#FFFFFF","#22D3EE","#10B981","#EC4899","#8B5CF6","#EF4444","#E11D48"].map(c=>(
                <button key={c} type="button" onClick={()=>setCfg({...cfg, secondary_color: c})} className={`w-7 h-7 rounded-full border-2 transition ${cfg.secondary_color?.toLowerCase()===c.toLowerCase() ? "border-slate-900 scale-110" : "border-slate-200 shadow"}`} style={{ background: c }} data-testid={`landing-secondary-preset-${c.slice(1)}`}/>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">Aplicada em botões CTA, badges e destaques. A cor principal continua nos títulos e detalhes.</p>
          </div>
        </div>

        {/* Preview */}
        <div className="mt-5 p-4 rounded-xl border border-slate-200 bg-slate-950">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: cfg.primary_color || "#E11D48" }}>Preview</div>
          <div className="font-heading text-2xl font-black uppercase leading-tight" style={{ color: cfg.primary_color || "#E11D48" }}>Título em cor principal</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button className="h-10 px-5 rounded-xl font-heading font-black uppercase text-sm text-white" style={{ background: cfg.secondary_color || "#F59E0B" }}>Botão CTA</button>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] uppercase font-bold text-white" style={{ background: cfg.secondary_color || "#F59E0B" }}>Badge</span>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="w-5 h-5 text-rose-600"/>
          <h3 className="font-heading font-bold text-slate-900">Fotos da academia (carrossel do topo)</h3>
          <span className="text-xs text-slate-500">Melhor 16:9 · até 4MB cada</span>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-bold uppercase hover:bg-black" data-testid="landing-upload">
          <Upload className="w-4 h-4"/>Adicionar fotos
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e)=>uploadImages(e.target.files)}/>
        </label>
        {cfg.hero_images?.length > 0 && (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4" data-testid="landing-hero-list">
            {cfg.hero_images.map((img, i) => (
              <div key={i} className="relative aspect-video rounded-lg overflow-hidden border border-slate-200 group">
                <img src={img} alt={`Foto ${i+1}`} className="w-full h-full object-cover"/>
                <button onClick={()=>removeImage(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100" data-testid={`landing-remove-img-${i}`}>
                  <X className="w-4 h-4"/>
                </button>
                <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold">Foto {i+1}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 border-slate-200">
        <h3 className="font-heading font-bold text-slate-900 mb-3">Benefícios (listagem)</h3>
        <div className="space-y-2 mb-3">
          {(cfg.features || []).map((f, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded bg-slate-50">
              <span className="flex-1 text-sm text-slate-700">{f}</span>
              <Button variant="ghost" size="sm" onClick={()=>removeFeature(i)}><Trash2 className="w-4 h-4 text-rose-500"/></Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newFeature} onChange={(e)=>setNewFeature(e.target.value)} placeholder="Ex: Aulas coletivas ilimitadas" onKeyDown={(e)=>e.key === "Enter" && addFeature()} data-testid="landing-new-feature"/>
          <Button onClick={addFeature} className="bg-rose-600 hover:bg-rose-700" data-testid="landing-add-feature"><Plus className="w-4 h-4"/></Button>
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <h3 className="font-heading font-bold text-slate-900 mb-3">Contato</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Telefone</Label><Input value={cfg.contact_phone || ""} onChange={(e)=>setCfg({...cfg, contact_phone: e.target.value})} placeholder="(11) 4000-0000" data-testid="landing-phone"/></div>
          <div><Label>WhatsApp</Label><Input value={cfg.contact_whatsapp || ""} onChange={(e)=>setCfg({...cfg, contact_whatsapp: e.target.value})} placeholder="11999999999" data-testid="landing-wa"/></div>
          <div><Label>E-mail</Label><Input value={cfg.contact_email || ""} onChange={(e)=>setCfg({...cfg, contact_email: e.target.value})} placeholder="contato@academia.com" data-testid="landing-email"/></div>
          <div><Label>Instagram</Label><Input value={cfg.instagram || ""} onChange={(e)=>setCfg({...cfg, instagram: e.target.value})} placeholder="@americanfit" data-testid="landing-ig"/></div>
          <div className="md:col-span-2"><Label>Endereço geral / sede</Label><Input value={cfg.contact_address || ""} onChange={(e)=>setCfg({...cfg, contact_address: e.target.value})} data-testid="landing-addr"/></div>
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <h3 className="font-heading font-bold text-slate-900 mb-1">Planos exibidos no site</h3>
        <p className="text-xs text-slate-500 mb-3">Marque quais planos aparecem para venda. Se nenhum for marcado, todos aparecem.</p>
        <div className="grid md:grid-cols-2 gap-2">
          {plans.map((p) => {
            const on = (cfg.plans_visible || []).includes(p.id);
            return (
              <button key={p.id} onClick={()=>togglePlan(p.id)} data-testid={`landing-plan-toggle-${p.id}`}
                className={`text-left p-3 rounded-lg border-2 transition ${on ? "border-rose-500 bg-rose-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">R$ {Number(p.price).toFixed(2)} · {p.duration_days ? `${p.duration_days}d` : `${p.duration_months}m`}</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${on ? "border-rose-500 bg-rose-500" : "border-slate-300"}`}>
                    {on && <div className="w-2 h-2 rounded-full bg-white"/>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
